// # Filename: src/components/friends/FriendsSidebar.jsx
import React, { useEffect, useCallback, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LuBellRing,
  LuChevronLeft,
  LuChevronRight,
  LuHouse,
  LuMedal,
  LuMenu,
  LuMessagesSquare,
  LuTrophy,
  LuUsers,
} from "react-icons/lu";

import { useFriends } from "../../context/friendsContext";
import { useDirectMessage } from "../../context/directMessageContext";
import { useUserContext } from "../../context/userContext";
import { useUI } from "../../context/uiContext";
import { useAuth } from "../../auth/hooks/useAuth";
import { useInviteContext } from "../../context/inviteContext";

import AccountPanel from "../user/AcountPanel";
import SidebarQuickActions from "./SidebarQuickActions";
import FriendsPanel from "./FriendsPanel";
import PendingRequestsPanel from "./PendingRequestPanel";
import InvitePanelContainer from "../notifications/InvitePanelContainer";
import Tooltip from "../ui/Tooltip";

import { createInvite } from "../../api/inviteApi";
import { resolveRecipientUserId } from "../../invites/resolveRecipientUserId";
import { buildInviteLobbyUrl } from "../../invites/InviteNavigation";
import useActiveLobbyId from "../lobby/hooks/useActiveLobbyId";
import { showToast } from "../../utils/toast/Toast";

export default function FriendsSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { lobbyId: activeLobbyId, gameType: activeLobbyGameType } = useActiveLobbyId();

  const {
    isSidebarOpen,
    setSidebarOpen,
    setDMOpen,
    isDMOpen,
    isSidebarCollapsed,
    setSidebarCollapsed,
  } = useUI();
  const { friends, pending, acceptRequest, declineRequest, removeFriend, refreshFriends } = useFriends();
  const { openChat, unreadCounts, groupUnreadCounts } = useDirectMessage();
  const { pendingInvites } = useInviteContext();
  const { isLoggedIn, user } = useUserContext();
  const { logout } = useAuth();

  const [isLgUp, setIsLgUp] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 1024px)").matches : true
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = () => setIsLgUp(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  const isMobile = !isLgUp;

  const closeSidebar = useCallback(() => setSidebarOpen(false), [setSidebarOpen]);
  const collapseDesktopSidebar = useCallback(() => setSidebarCollapsed(true), [setSidebarCollapsed]);

  useEffect(() => {
    if (isMobile && !isSidebarOpen) return;
    if (!isMobile && isSidebarCollapsed) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (isMobile) closeSidebar();
      else collapseDesktopSidebar();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isMobile, isSidebarOpen, isSidebarCollapsed, closeSidebar, collapseDesktopSidebar]);

  useEffect(() => {
    if (!isMobile || !isSidebarOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [isMobile, isSidebarOpen]);

  useEffect(() => {
    if (!isMobile) return;
    closeSidebar();
  }, [location.pathname, isMobile, closeSidebar]);

  useEffect(() => { refreshFriends(); }, [refreshFriends]);

  const [pendingFriend, setPendingFriend] = useState(null);

  const handleOpenDM = useCallback((friend) => {
    if (!friend) return;
    setPendingFriend(friend);
    setDMOpen(true);
    if (isMobile) setSidebarOpen(false);
  }, [setDMOpen, isMobile, setSidebarOpen]);

  useEffect(() => {
    if (!isDMOpen || !pendingFriend) return;
    const t = setTimeout(async () => {
      try { await openChat(pendingFriend); }
      catch (err) { console.error("[FriendsSidebar] openChat failed:", err); }
      finally { setPendingFriend(null); }
    }, 0);
    return () => clearTimeout(t);
  }, [isDMOpen, pendingFriend, openChat]);

  const handleInvite = useCallback(async (friend, gameType = "tic_tac_toe") => {
    try {
      if (!user?.id) return;
      const recipientUserId = resolveRecipientUserId(friend, user.id);
      if (!recipientUserId || Number(recipientUserId) === Number(user.id)) return;

      // Only reuse the currently-open lobby if it's for the same game type
      // the user picked -- otherwise a fresh lobby/game gets created.
      const useActiveLobby = Boolean(activeLobbyId) && activeLobbyGameType === gameType;

      const result = await createInvite({
        toUserId: recipientUserId,
        gameType,
        lobbyId: useActiveLobby ? activeLobbyId : undefined,
      });
      const lobbyId = result?.lobbyId || result?.lobby_id || result?.gameId || result?.game_id || (useActiveLobby ? activeLobbyId : undefined);
      const inviteId = result?.invite?.inviteId || result?.invite?.id || result?.inviteId || result?.invite_id;
      if (!lobbyId || !inviteId) { showToast("error", "Invite failed."); return; }
      if (useActiveLobby) { showToast("success", "Invite sent!"); return; }
      navigate(buildInviteLobbyUrl({ gameType, lobbyId, inviteId }));
      if (isMobile) closeSidebar();
    } catch { showToast("error", "Invite failed."); }
  }, [navigate, user?.id, activeLobbyId, activeLobbyGameType, isMobile, closeSidebar]);

  const pendingReceived = pending?.received || [];

  const totalChatUnread = useMemo(() => {
    const dmTotal = Object.values(unreadCounts || {}).reduce((sum, n) => sum + Number(n || 0), 0);
    const groupTotal = Object.values(groupUnreadCounts || {}).reduce((sum, n) => sum + Number(n || 0), 0);
    return dmTotal + groupTotal;
  }, [unreadCounts, groupUnreadCounts]);

  const handleOpenChatInbox = useCallback(() => {
    setDMOpen(true);
    if (isMobile) setSidebarOpen(false);
  }, [setDMOpen, isMobile, setSidebarOpen]);

  const handleAccept = useCallback(async (id) => {
    try { await acceptRequest(id); refreshFriends(); }
    catch (err) { console.error("Failed to accept request:", err); }
  }, [acceptRequest, refreshFriends]);

  const handleDecline = useCallback(async (id) => {
    try { await declineRequest(id); refreshFriends(); }
    catch (err) { console.error("Failed to decline request:", err); }
  }, [declineRequest, refreshFriends]);

  const handleUnfriend = useCallback(async (friend) => {
    const id = friend?.id;
    if (!id) return;
    try { await removeFriend(id); }
    catch (err) { console.error("Failed to remove friend:", err); }
  }, [removeFriend]);

  const handleLogin       = useCallback(() => { navigate("/login");       if (isMobile) closeSidebar(); }, [navigate, isMobile, closeSidebar]);
  const handleLeaderboard = useCallback(() => { navigate("/leaderboard"); if (isMobile) closeSidebar(); }, [navigate, isMobile, closeSidebar]);
  const handleTournaments = useCallback(() => { navigate("/tournaments"); if (isMobile) closeSidebar(); }, [navigate, isMobile, closeSidebar]);
  const handleHome = useCallback(() => { navigate("/"); if (isMobile) closeSidebar(); }, [navigate, isMobile, closeSidebar]);

  const overlayClassName = useMemo(() => {
    if (!isMobile) {
      return [
        "hidden lg:block absolute left-[72px] top-0 z-[60] h-full",
        "w-[calc(100vw-72px)] max-w-[calc(100vw-72px)]",
        "bg-black/10 transition-opacity",
        isSidebarCollapsed ? "opacity-0 pointer-events-none" : "opacity-100 pointer-events-auto",
      ].join(" ");
    }
    return [
      "fixed left-0 right-0 z-[55]",
      "top-[60px] sm:top-[64px]",
      "h-[calc(100dvh-60px)] sm:h-[calc(100dvh-64px)]",
      "bg-black/70 backdrop-blur-sm transition-opacity",
      isSidebarOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none",
    ].join(" ");
  }, [isMobile, isSidebarOpen, isSidebarCollapsed]);

  const drawerClassName = useMemo(() => {
    const openClose = isSidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0";
    return [
      "text-text-primary flex flex-col",
      "transform transition-transform duration-300 ease-out",
      openClose,
      "fixed left-0 z-[70]",
      "top-[60px] sm:top-[64px]",
      "h-[calc(100dvh-60px)] sm:h-[calc(100dvh-64px)]",
      "w-[88vw] max-w-[360px]",
      isSidebarCollapsed
        ? "lg:static lg:z-auto lg:h-full lg:w-full lg:max-w-none lg:shadow-none"
        : "lg:absolute lg:left-0 lg:top-0 lg:z-[80] lg:h-full lg:w-[280px] lg:max-w-none lg:translate-x-0 lg:shadow-[22px_0_70px_rgba(0,0,0,0.55)]",
    ].join(" ");
  }, [isSidebarOpen, isSidebarCollapsed]);

  const collapsedRail = !isMobile && isSidebarCollapsed;

  return (
    <>
      <button
        type="button"
        aria-label="Close sidebar"
        onClick={isMobile ? closeSidebar : collapseDesktopSidebar}
        className={overlayClassName}
      />

      <aside
        className={`${drawerClassName} bg-background-app-panel border-r border-border-soft`}
        role={isMobile ? "dialog" : undefined}
        aria-modal={isMobile ? "true" : undefined}
        aria-label="Social sidebar"
      >
        {collapsedRail ? (
          <DesktopRail
            user={user}
            pendingCount={pendingReceived.length}
            inviteCount={pendingInvites.length}
            chatUnread={totalChatUnread}
            pathname={location.pathname}
            onExpand={() => setSidebarCollapsed(false)}
            onHome={handleHome}
            onFriends={() => setSidebarCollapsed(false)}
            onInvites={() => setSidebarCollapsed(false)}
            onChat={handleOpenChatInbox}
            onTournaments={handleTournaments}
            onLeaderboard={handleLeaderboard}
          />
        ) : (
          <>
        {/* Mobile header */}
        <div className="lg:hidden sticky top-0 z-10 bg-background-app-panel border-b border-border-soft">
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-[10px] tracking-[0.35em] font-semibold uppercase text-text-muted">
              Menu
            </span>
            <button
              type="button"
              onClick={closeSidebar}
              className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider rounded-button bg-surface border border-border-soft text-text-secondary transition-colors hover:bg-surface-elevated hover:text-text-primary"
            >
              Close
            </button>
          </div>
        </div>

        {/* Desktop label */}
        <div className="hidden lg:flex items-center justify-between gap-2 px-4 pt-5 pb-3 border-b border-border-soft">
          <span className="text-[10px] tracking-[0.35em] font-semibold uppercase text-text-muted">
            Social
          </span>
          <Tooltip content="Collapse">
            <button
              type="button"
              onClick={collapseDesktopSidebar}
              className="grid h-8 w-8 place-items-center rounded-lg border border-border-soft bg-surface text-text-muted transition hover:bg-surface-elevated hover:text-brand-cyan"
              aria-label="Collapse social sidebar"
            >
              <LuChevronLeft size={15} />
            </button>
          </Tooltip>
        </div>

        {/* Quick actions toolbar */}
        <div className="px-3 pt-4">
          <SidebarQuickActions
            onOpenChatInbox={handleOpenChatInbox}
            chatUnread={totalChatUnread}
            onOpenLeaderboard={handleLeaderboard}
          />
        </div>

        {/* Content -- flat divided list, no per-section boxes */}
        <div className="flex-1 overflow-y-auto lol-scrollbar">
          <div className="px-3 py-3 divide-y divide-border-soft/60">
            <div className="py-3 first:pt-0">
              <InvitePanelContainer />
            </div>
            <div className="py-3">
              <FriendsPanel
                friends={friends}
                user={user}
                onFriendClick={handleOpenDM}
                onChatOpen={handleOpenDM}
                onInvite={handleInvite}
                onUnfriend={handleUnfriend}
                unreadCounts={unreadCounts}
              />
            </div>
            <div className="py-3 last:pb-0">
              <PendingRequestsPanel
                requests={pendingReceived}
                onAccept={handleAccept}
                onDecline={handleDecline}
              />
            </div>
          </div>
        </div>

        {/* Sticky account footer */}
        <div className="shrink-0 border-t border-border-soft px-3 py-3 bg-background-app-panel">
          <AccountPanel
            isLoggedIn={isLoggedIn}
            user={user}
            onLogin={handleLogin}
            onLogout={logout}
          />
        </div>
          </>
        )}
      </aside>
    </>
  );
}

function RailButton({ icon, label, badge = 0, onClick, active = false, urgent = false, accent = "cyan" }) {
  const isAmber = accent === "amber";
  return (
    <Tooltip content={label} position="right">
      <button
        type="button"
        onClick={onClick}
        className={[
          "relative grid h-11 w-11 place-items-center rounded-2xl border transition",
          active
            ? isAmber
              ? "border-brand-amber/35 bg-brand-amber/10 text-brand-amber shadow-[0_0_28px_rgba(251,191,36,0.12)]"
              : "border-brand-cyan/30 bg-brand-cyan/10 text-brand-cyan shadow-[0_0_28px_rgba(34,211,238,0.12)]"
            : urgent
              ? "border-brand-amber/35 bg-brand-amber/10 text-brand-amber shadow-[0_0_24px_rgba(251,191,36,0.18)] hover:bg-brand-amber/15"
              : "border-transparent text-text-muted hover:border-border-soft hover:bg-surface hover:text-brand-cyan",
        ].join(" ")}
        aria-label={label}
      >
        {urgent ? (
          <span className="absolute inset-0 rounded-2xl border border-brand-amber/30 animate-pulse" />
        ) : null}
        {icon}
        {badge > 0 ? (
          <span className={[
            "absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-bold leading-none text-background-app",
            urgent || isAmber ? "bg-brand-amber" : "bg-brand-cyan",
          ].join(" ")}>
            {badge > 9 ? "9+" : badge}
          </span>
        ) : null}
      </button>
    </Tooltip>
  );
}

function DesktopRail({
  user,
  pendingCount,
  inviteCount,
  chatUnread,
  pathname,
  onExpand,
  onHome,
  onFriends,
  onInvites,
  onChat,
  onTournaments,
  onLeaderboard,
}) {
  const initial = (user?.first_name || user?.email || "?").trim().charAt(0).toUpperCase() || "?";
  return (
    <div className="hidden h-full flex-col items-center justify-between py-3 lg:flex">
      <div className="flex flex-col items-center gap-3">
        <Tooltip content="Expand" position="right">
          <button
            type="button"
            onClick={onExpand}
            className="grid h-10 w-10 place-items-center rounded-xl border border-border-soft bg-surface text-text-secondary transition hover:bg-surface-elevated hover:text-brand-cyan"
            aria-label="Expand social sidebar"
          >
            <LuChevronRight size={16} />
          </button>
        </Tooltip>

        <div className="grid h-10 w-10 place-items-center rounded-xl border border-brand-cyan/20 bg-brand-cyan/10 text-sm font-bold text-brand-cyan">
          {initial}
        </div>

        <div className="h-px w-10 bg-border-soft" />

        <RailButton icon={<LuHouse size={20} strokeWidth={2.1} />} label="Home" active={pathname === "/"} onClick={onHome} />
        <RailButton icon={<LuUsers size={20} strokeWidth={2.1} />} label="Friends" badge={pendingCount} onClick={onFriends} />
        <RailButton icon={<LuBellRing size={20} strokeWidth={2.1} />} label="Invites" badge={inviteCount} urgent={inviteCount > 0} onClick={onInvites} />
        <RailButton icon={<LuMessagesSquare size={20} strokeWidth={2.1} />} label="Chat" badge={chatUnread} onClick={onChat} />
        <RailButton icon={<LuTrophy size={20} strokeWidth={2.1} />} label="Tournaments" accent="amber" active={pathname.startsWith("/tournaments")} onClick={onTournaments} />
        <RailButton icon={<LuMedal size={20} strokeWidth={2.1} />} label="Ranks" active={pathname.startsWith("/leaderboard")} onClick={onLeaderboard} />
      </div>

      <RailButton icon={<LuMenu size={20} strokeWidth={2.1} />} label="Expand menu" onClick={onExpand} />
    </div>
  );
}
